/**
 * LANE JOB-TRUST — pure path math for the 6-axis fit radar (RadarChart.tsx).
 * Extracted so the geometry is unit-testable without a DOM/SVG harness
 * (mirrors this app's house idiom — see presenceHonestRender.ts's own
 * "pure logic out of JSX" doc comment). NO INVENTION: this module never
 * decides WHICH axes exist or WHAT their values are — it only lays out
 * whatever `Record<string, number>` it is given. A caller that wants "never
 * fabricate an axis" honesty enforces that by not calling this at all when
 * `radarAxes` is null (RadarChart.tsx's own job, not this file's).
 */

export interface RadarPoint {
  x: number;
  y: number;
}

/** One vertex per axis, evenly spaced starting at 12 o'clock (matches the
 *  familiar radar/spider-chart convention), scaled by the axis's own 0-100
 *  value against `radius`. Values are clamped to [0, 100] — a value outside
 *  that range (should never happen; served scores are already 0-100) is
 *  clamped rather than allowed to draw outside the grid or invert. */
export function axisPoints(
  values: number[],
  center: number,
  radius: number,
): RadarPoint[] {
  const n = values.length;
  if (n === 0) return [];
  return values.map((raw, i) => {
    const clamped = Math.max(0, Math.min(100, raw));
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const r = (clamped / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  });
}

/** The full-scale grid vertices (every axis at 100) — the outer polygon
 *  outline every ring/spoke is drawn against. */
export function gridPoints(axisCount: number, center: number, radius: number): RadarPoint[] {
  return axisPoints(Array(axisCount).fill(100), center, radius);
}

/** An SVG `points` attribute string ("x1,y1 x2,y2 ...") from a point list —
 *  shared by the data polygon and the grid rings. */
export function toPolygonString(points: RadarPoint[]): string {
  return points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

/** One grid ring at a given scale (0-1 of the full radius) — used to draw
 *  the concentric reference rings (e.g. at 25/50/75/100%). */
export function ringPoints(axisCount: number, center: number, radius: number, scale: number): RadarPoint[] {
  return axisPoints(Array(axisCount).fill(100 * scale), center, radius);
}

/** The label anchor point for one axis — placed slightly OUTSIDE the
 *  full-scale grid vertex (at `labelRadius`, normally > radius) so text never
 *  overlaps the chart itself. */
export function labelPoints(axisCount: number, center: number, labelRadius: number): RadarPoint[] {
  return axisPoints(Array(axisCount).fill(100), center, labelRadius);
}

/** Given a served `Record<string, number>`, return the axis keys in a STABLE
 *  order (insertion order, i.e. `Object.keys` — the order the server wrote
 *  them in, never re-sorted/re-labeled by this app) alongside their values.
 *  Pure, no invention: this never adds, drops, or renames an axis. */
export function orderedAxes(radarAxes: Record<string, number>): { key: string; value: number }[] {
  return Object.keys(radarAxes).map((key) => ({ key, value: radarAxes[key] }));
}

/** Humanize a served axis key for display ("culture_fit" -> "Culture fit",
 *  "seniorityMatch" -> "Seniority match") — presentation only, the key
 *  itself is never altered or reordered. Handles both snake_case and
 *  camelCase since the server's radar_axes keys are not guaranteed to be one
 *  or the other across every rubric version. */
export function humanizeAxisLabel(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}
